import React, { useEffect, useState } from "react";
import { Phone, ChevronDown, Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const SERVICES = [
  { to: "/housekeepers", label: "Housekeepers", testId: "nav-svc-housekeepers" },
  { to: "/nanny-agency", label: "Nannies", testId: "nav-svc-nanny-agency" },
  { to: "/household-managers", label: "Household Managers", testId: "nav-svc-household-managers" },
  { to: "/estate-manager", label: "Estate Managers", testId: "nav-svc-estate-manager" },
  { to: "/services/private-chef", label: "Private Chefs", testId: "nav-svc-private-chefs" },
  { to: "/services/personal-assistant", label: "Personal Assistants", testId: "nav-svc-personal-assistants" },
];

const LOCATIONS = [
  { to: "/florida", label: "Florida (Statewide)" },
  { to: "/regions/sarasota", label: "Sarasota — Headquarters" },
  { to: "/regions/tampa", label: "Tampa" },
  { to: "/regions/naples", label: "Naples" },
  { to: "/regions/palm-beach", label: "Palm Beach" },
  { to: "/regions/miami", label: "Miami" },
  { to: "/regions/fort-lauderdale", label: "Fort Lauderdale" },
  { to: "/regions/orlando", label: "Orlando" },
  { to: "/regions/jacksonville", label: "Jacksonville" },
];

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobile(false);
    setOpen(null);
  }, [location.pathname]);

  const drop = (id, label, items, testId) => (
    <div className="relative" onMouseEnter={() => setOpen(id)} onMouseLeave={() => setOpen(null)}>
      <button
        data-testid={testId}
        onClick={() => setOpen(open === id ? null : id)}
        className="flex items-center gap-1.5 link-line py-2"
        aria-expanded={open === id}
      >
        {label}
        <ChevronDown className={`w-3.5 h-3.5 text-[#B08D57] transition-transform duration-300 ${open === id ? "rotate-180" : ""}`} strokeWidth={2} />
      </button>
      {open === id && (
        <div className="absolute top-full left-0 pt-2 min-w-[240px] z-50">
          <div className="bg-white/95 backdrop-blur-xl border border-slate-200/80 rounded-2xl shadow-xl p-2">
            {items.map((i) => (
              <Link
                key={i.to}
                to={i.to}
                data-testid={i.testId || `nav-loc-${i.to.split("/").pop()}`}
                className="block px-4 py-3 text-sm text-[#33302A] rounded-xl hover:bg-[#F5F0EB] hover:text-[#C86D51] transition-colors duration-200"
              >
                {i.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled || mobile
          ? "bg-[#FAF7F1]/95 backdrop-blur-xl border-b border-[#E9E2D4] py-4"
          : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-logo" aria-label="Elite Swiss — Home" className="font-serif-le text-2xl sm:text-3xl text-[#33302A]">
          Elite <span className="italic text-[#B08D57]">Swiss</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-7 text-sm text-[#33302A]/80 font-medium">
          <Link data-testid="nav-home-link" to="/" className="link-line">Home</Link>
          <Link data-testid="nav-about-link" to="/about" className="link-line">About Us</Link>
          {drop("services", "Hire Household Staff", SERVICES, "nav-services-link")}
          {drop("locations", "Locations", LOCATIONS, "nav-locations-link")}
          <Link data-testid="nav-candidates-link" to="/candidates" className="link-line">For Candidates</Link>
          <Link data-testid="nav-contact-link" to="/contact" className="link-line">Contact Us</Link>
        </nav>
        <div className="flex items-center gap-4">
          <a
            data-testid="nav-phone-link"
            href="tel:+13055294140"
            className="hidden md:flex items-center gap-2 text-sm text-[#33302A]/80 font-medium hover:text-[#C86D51] transition-colors duration-200"
          >
            <Phone className="w-4 h-4 text-[#C86D51]" strokeWidth={1.5} />
            +1 (305) 529-4140
          </a>
          <Link
            to="/contact"
            data-testid="nav-inquire-button"
            className="btn-warm px-7 py-2.5 text-sm font-semibold tracking-wide"
          >
            Inquire
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-2 text-[#33302A]"
            onClick={() => setMobile(!mobile)}
            aria-label="Menu"
          >
            {mobile ? <X className="w-6 h-6" strokeWidth={1.5} /> : <Menu className="w-6 h-6" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {mobile && (
        <div data-testid="nav-mobile-menu" className="lg:hidden bg-[#FAF7F1] border-t border-[#E9E2D4] px-6 py-6 max-h-[80vh] overflow-y-auto">
          <div className="space-y-1">
            <Link to="/" data-testid="nav-mobile-home" className="block py-3 text-base font-medium text-[#33302A]">Home</Link>
            <Link to="/about" data-testid="nav-mobile-about" className="block py-3 text-base font-medium text-[#33302A]">About Us</Link>
            <p className="pt-4 pb-1 text-[11px] uppercase tracking-[0.2em] text-[#8A7F6D]">Hire Household Staff</p>
            {SERVICES.map((s) => (
              <Link key={s.to} to={s.to} data-testid={`nav-mobile-${s.to.replace(/\//g, "-").slice(1)}`} className="block py-2.5 pl-3 text-[#33302A]/80 border-l border-[#B08D57]/30">
                {s.label}
              </Link>
            ))}
            <p className="pt-4 pb-1 text-[11px] uppercase tracking-[0.2em] text-[#8A7F6D]">Locations</p>
            {LOCATIONS.map((loc) => (
              <Link key={loc.to} to={loc.to} data-testid={`nav-mobile-loc-${loc.to.split("/").pop()}`} className="block py-2.5 pl-3 text-[#33302A]/80 border-l border-[#B08D57]/30">
                {loc.label}
              </Link>
            ))}
            <div className="pt-4 space-y-1 border-t border-[#E9E2D4] mt-4">
              <Link to="/candidates" data-testid="nav-mobile-candidates" className="block py-3 text-base font-medium text-[#33302A]">For Candidates</Link>
              <Link to="/contact" data-testid="nav-mobile-contact" className="block py-3 text-base font-medium text-[#33302A]">Contact Us</Link>
              <Link to="/blog" data-testid="nav-mobile-blog" className="block py-3 text-base font-medium text-[#33302A]">Journal</Link>
              <a href="tel:+13055294140" data-testid="nav-mobile-phone" className="flex items-center gap-2 py-3 text-[#33302A]/80">
                <Phone className="w-4 h-4 text-[#C86D51]" strokeWidth={1.5} /> +1 (305) 529-4140
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Nav;
